<template>
  <BrokenContextElement
    v-if="scopedContextIsBroken"
    :component-name="componentLabel"
    :problems="scopedContextProblems"
  />
  <DynamicComponentProductionElement
    v-else-if="canRender && scopedContextGatePasses"
    :dynamic-component-id="dynamicComponentId"
    :element-id="rootElementId"
    :is-production="renderingInProduction"
    :theme-mode="themeMode"
  />
</template>

<script>
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import DynamicComponentProductionElement
  from "%/dynamic-component-editor/components/DynamicComponentProductionElement.vue";
import BrokenContextElement from "%/dynamic-component-editor/components/BrokenContextElement.vue";
import ScopedProvidesContextsMixin from "%/dynamic-component-editor/mixins/scoped-provides-contexts-mixin";
import { readRequiredContexts, readProvidesContexts } from "@studio/Helpers/studio-installed-apps-bootstrap";
import {
  bareEntityType,
  bareRequiredContextEntityType,
} from "%/dynamic-component-editor/helpers/required-context-entity-type";
import { resolvesScopedProvides } from "%/dynamic-component-editor/helpers/resolves-scoped-provides";

const EMPTY_RELEASE_WARNING_DELAY_MS = 5000;

let nextFeedRowInstanceId = 0;

export default {
  name: "AppComponentOverrideHost",
  components: { DynamicComponentProductionElement, BrokenContextElement },
  mixins: [ScopedProvidesContextsMixin],
  provide() {
    return {
      dcInstanceKey: `feed-row-${nextFeedRowInstanceId++}`,
      inFeedRow: true,
    };
  },
  inject: {
    renderingInProduction: {
      from: "renderingInProduction",
      default: false,
    },
  },
  props: {
    appName: {
      type: String,
      required: true,
    },
    componentName: {
      type: String,
      default: "",
    },
    dynamicComponentId: {
      type: [String, Number],
      required: true,
    },
    contexts: {
      type: Object,
      default: () => ({}),
    },
    themeMode: {
      type: String,
      default: "",
    },
  },
  data() {
    return { emptyReleaseWarningTimer: null };
  },
  computed: {
    componentLabel() {
      return this.componentName || `component ${this.dynamicComponentId}`;
    },
    referencedComponent() {
      return DynamicComponent.get(this.dynamicComponentId, this.renderingInProduction);
    },
    rootElementId() {
      return this.referencedComponent?.rootElementId ?? null;
    },
    requiredContexts() {
      return this.referencedComponent ? readRequiredContexts(this.referencedComponent) : {};
    },
    hasAllRequiredContexts() {
      return Object.entries(this.requiredContexts).every(([name, def]) => {
        if (def && Object.prototype.hasOwnProperty.call(def, "default")) {
          return true;
        }
        const supplied = this.contexts?.[name];
        if (supplied === undefined || supplied === null) {
          return false;
        }
        const wantedType = bareRequiredContextEntityType(def);
        return !wantedType || bareEntityType(supplied.entityType) === wantedType;
      });
    },
    canRender() {
      return Boolean(this.rootElementId)
        && this.hasAllRequiredContexts;
    },
    shouldResolveScopedProvides() {
      return resolvesScopedProvides({
        renderingInProduction: this.renderingInProduction,
        blclientData: typeof window === "undefined" ? null : window.blclient?.data,
      });
    },
  },
  watch: {
    "referencedComponent.rootElementId": {
      immediate: true,
      handler() {
        this.seedContexts();
        if (this.shouldResolveScopedProvides && this.referencedComponent) {
          this.resolveScopedProvidedContexts(this.referencedComponent, String(this.dynamicComponentId));
        }
      },
    },
    contexts: {
      deep: true,
      handler() {
        this.seedContexts();
      },
    },
  },
  created() {
    this.seedContexts();
  },
  mounted() {
    if (!this.renderingInProduction) {
      return;
    }
    this.emptyReleaseWarningTimer = setTimeout(() => {
      if (this.rootElementId !== null || !this.referencedComponent) {
        return;
      }
      // eslint-disable-next-line no-console
      console.error(
        `AppComponentOverrideHost: materialized copy DC ${this.dynamicComponentId} `
        + `("${this.componentLabel}", app "${this.appName}") has no published release — `
        + "its rows render EMPTY on this published page. Publish the destination so "
        + "compile snapshots the copy into a DynamicComponentRelease.",
        { dynamicComponentId: this.dynamicComponentId, componentName: this.componentLabel, appName: this.appName },
      );
    }, EMPTY_RELEASE_WARNING_DELAY_MS);
  },
  unmounted() {
    if (this.emptyReleaseWarningTimer) {
      clearTimeout(this.emptyReleaseWarningTimer);
      this.emptyReleaseWarningTimer = null;
    }
  },
  methods: {
    scopedContextComponentDefs() {
      if (!this.referencedComponent) {
        return null;
      }
      return {
        componentName: this.componentLabel,
        required: readRequiredContexts(this.referencedComponent),
        provides: readProvidesContexts(this.referencedComponent),
      };
    },
    seedContexts() {
      const seeded = {};
      Object.entries(this.contexts ?? {}).forEach(([name, item]) => {
        if (!item) {
          return;
        }
        seeded[name] = {
          entityType: item.entityType,
          entityPkUrlVars: item.pk ?? item.entityPkUrlVars,
          appName: item.appName ?? this.appName,
        };
      });
      this.scopedContexts = { ...this.scopedContexts, ...seeded };
    },
    lookupExtraScopedContext(name) {
      const item = this.contexts?.[name];
      if (!item) {
        return null;
      }
      return {
        entityType: item.entityType,
        entityPkUrlVars: item.pk ?? item.entityPkUrlVars,
        appName: item.appName ?? this.appName,
      };
    },
  },
};
</script>
